import { useSelectedChart } from "@/contexts/ChartsContext";
import { Colors } from "@/enums/Colors";
import { MainDashBoardChart } from "@/enums/MainDashBoardChart";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { MonthlyChartDTO } from "@/models/charts/MonthlySpendingChartDTO";
import { Box, BoxProps } from "@mui/material";
import React from "react";
import { Bar } from "react-chartjs-2";
import { AskAIButtonProps, ChartTopBar } from "./ChartTopBar";

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, LineController, BarController);

interface Props {
  data: MonthlyChartDTO;
  containerProps?: BoxProps;
  disableAskAIButton?: boolean;
}

interface PropsToHandleInChat {
  refer?: React.RefObject<ChartJS>;
  chartOptions?: ChartOptions;
}

const LineColor = Colors.ORANGE_FF9A3D;
const BarColor = Colors.BLUE_4763F4;

enum KEYS {
  NET_BURN = "NET_BURN",
  CASH_BALANCE = "CASH_BALANCE",
}

function formatNumber(value: number) {
  if (value >= 1000000) {
    return Math.floor(value / 1000000) + "M";
  } else if (value >= 1000 || value <= -1000) {
    return Math.floor(value / 1000) + "K";
  } else {
    return value.toString();
  }
}

function formatMonthYear(months: string[]): string[] {
  const formattedDates = months.map((date) => {
    const [month, year] = date.split(" ");
    const abbreviatedMonth = month.slice(0, 3);
    const abbreviatedYear = year?.slice(2);
    return `${abbreviatedMonth} ‘${abbreviatedYear}`;
  });
  return formattedDates;
}

const NetBurnAndBalanceChart = ({
  data: monthlyNetBurnAndCashBalance,
  containerProps,
  disableAskAIButton,
  refer,
  chartOptions,
}: Props & AskAIButtonProps & PropsToHandleInChat) => {
  const { setSelectedChart } = useSelectedChart();

  const mapData = (chartDto: MonthlyChartDTO): any => {
    let datasets: any = [];
    chartDto.series.forEach((serie) => {
      let values = chartDto.data.map((d) => d.values[serie.dataKey]).filter((value) => typeof value === "number");
      //Workaround to add space at beginning and end of X axis
      //@ts-ignore
      values.push(null);
      //@ts-ignore
      values.unshift(null);

      let dataset = {
        type: serie.dataKey == KEYS.CASH_BALANCE ? "line" : "bar",
        label: StringUtils.toTitleCase(serie.dataKey),
        data: values,
        barThickness: 24,
        borderRadius: 2,
        yAxisID: serie.dataKey,
      };

      datasets.push(dataset);
    });

    const labels = formatMonthYear(chartDto.keys);
    //Workaround to add space at beginning and end of X axis
    labels.push("");
    labels.unshift("");

    return {
      labels,
      datasets: [datasets[1], datasets[0]],
    };
  };

  return (
    <Box sx={{ width: "100%", height: "90%", paddingBottom: "10px" }} {...containerProps}>
      <ChartTopBar
        disableAskAIButton={disableAskAIButton}
        onStarIconClick={() => setSelectedChart(MainDashBoardChart.NET_BURN_AND_BALANCE)}
        keys={[
          { color: BarColor, name: "Net Burn" },
          { color: LineColor, name: "Cash Balance" },
        ]}
        title="Net Burn & Cash Balance"
        cashBalanceCard
      />
      <Bar
        style={{ paddingTop: "20px" }}
        data={monthlyNetBurnAndCashBalance.data.length > 0 ? mapData(monthlyNetBurnAndCashBalance) : data}
        options={{ ...(options as any), ...chartOptions }}
        ref={refer as any}
        // plugins={[myPlugin]}
      />
    </Box>
  );
};

export default NetBurnAndBalanceChart;

const data = {
  labels: ["a", "b", "c", "d", "e", "f"],
  datasets: [
    {
      label: "Data",
      data: [10, 0, 0, 0, 0, 0, 40],
      borderWidth: 1,
    },
  ],
};

const scale_default = {
  border: {
    display: false,
  },
  ticks: {
    count: 5,
    autoSkip: false,
    align: "start",
    padding: -1,
    mirror: true,
    labelOffset: 6,
    font: {
      weight: 500,
      size: 12,
    },
  },
};

const options: ChartOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: "white",
      borderColor: "grey",
      borderWidth: 0.5,
      padding: 8,
      bodyColor: Colors.LIGHT_GRAY_8A8E93,
      titleColor: Colors.LIGHT_GRAY_8A8E93,
      boxWidth: 10,
      boxHeight: 10,
      titleMarginBottom: 16,
      filter({ ...args }) {
        return args.parsed.y != null;
      },
      callbacks: {
        labelColor: function (context) {
          return {
            borderColor: context.element.options.borderColor,
            backgroundColor: context.element.options.borderColor,
          };
        },
      },
    },
  },
  elements: {
    bar: { borderRadius: 2, backgroundColor: BarColor, borderColor: BarColor },
    point: {
      backgroundColor: "white",
      radius: 6,
      borderColor: LineColor,
      borderWidth: 3,
    },
    line: {
      borderWidth: 4,
      borderColor: LineColor,
    },
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      offset: false,
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
      ticks: {},
    },
    y: {
      display: false,
    },
    CASH_BALANCE: {
      ...scale_default,
      position: "right",

      grid: {
        lineWidth: 0.7,
      },
      ticks: {
        ...scale_default.ticks,
        color: LineColor,
        font: {
          ...scale_default.ticks.font,
        },

        callback: (value) => {
          return `$${formatNumber(value as number)}`;
        },
      },
    },
    NET_BURN: {
      ...scale_default,
      position: "left",
      grid: {
        display: false,
      },
      ticks: {
        ...scale_default.ticks,
        color: BarColor,
        font: {
          ...scale_default.ticks.font,
        },
        callback: (value) => {
          const formatedValue = formatNumber(value as number);
          if ((value as number) < 0) return `(${formatedValue})`;
          return formatedValue;
        },
      },
    },
  },
  interaction: {
    intersect: false,
    mode: "index",
  },
};
